export type JsonRpcParams = Record<string, unknown>;

export function buildJsonRpcBody(
  method: string,
  params: JsonRpcParams,
  sessionId?: string,
  database?: string,
  username?: string,
) {
  params = { ...params, credentials: { sessionId, database, userName: username } };
  return JSON.stringify({ method, params });
}

export async function callBinary(
  apiUrl: string,
  method: string,
  params: JsonRpcParams,
  sessionId?: string,
  database?: string,
  username?: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; contentType?: string; filename?: string }> {
  const res = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: buildJsonRpcBody(method, params, sessionId, database, username),
    signal,
  });

  const ct = res.headers.get("content-type") || undefined;
  const cd = res.headers.get("content-disposition") || "";

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || res.statusText || `HTTP ${res.status}`);
  }

  // If server accidentally returns JSON on 200, surface a meaningful error
  if (ct && ct.includes("application/json")) {
    const json = await res.text(); // keep as text to preserve error body
    throw new Error(`Unexpected JSON payload from ${method}: ${json.slice(0, 500)}`);
  }

  const blob = await res.blob();
  const filename = parseFilenameFromContentDisposition(cd);
  return { blob, contentType: ct, filename };
}

function parseFilenameFromContentDisposition(cd: string): string | undefined {
  // Content-Disposition: attachment; filename="report.pdf"
  const match = /filename\*?=(?:UTF-8''|")(.*?)"?$/i.exec(cd);
  if (!match) return undefined;
  try {
    // Handle RFC5987 encoding (UTF-8'')
    return decodeURIComponent(match[1].replace(/"/g, ""));
  } catch {
    return match[1];
  }
}
